import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/app/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import InvestorDashboard from "@/pages/InvestorDashboard";
import MyTokens from "@/pages/MyTokens";
import Marketplace from "@/pages/Marketplace";
import Transactions from "@/pages/Transactions";
import Notifications from "@/pages/Notifications";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import KYC from "@/pages/KYC";
import Referrals from "@/pages/Referrals";
import PaystackCallback from "@/pages/PaystackCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProperties from "@/pages/AdminProperties";
import AdminInvestors from "@/pages/AdminInvestors";
import AdminKYC from "@/pages/AdminKYC";
import AdminTokens from "@/pages/AdminTokens";
import BankTransfer from "@/pages/BankTransfer";
import AdminBankTransfers from "@/pages/AdminBankTransfers";
import AdminPaymentSettings from "@/pages/AdminPaymentSettings";

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#050914] flex items-center justify-center text-gray-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return children;
}

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Layout>
            <Routes>
              <Route path="/" element={<RoleHome />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/properties" element={<Properties />} />
              <Route path="/properties/:id" element={<PropertyDetail />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/paystack/callback" element={<PaystackCallback />} />

              <Route path="/dashboard" element={<ProtectedRoute role="investor"><InvestorDashboard /></ProtectedRoute>} />
              <Route path="/my-tokens" element={<ProtectedRoute role="investor"><MyTokens /></ProtectedRoute>} />
              <Route path="/kyc" element={<ProtectedRoute role="investor"><KYC /></ProtectedRoute>} />
              <Route path="/referrals" element={<ProtectedRoute role="investor"><Referrals /></ProtectedRoute>} />
              <Route path="/bank-transfer" element={<ProtectedRoute role="investor"><BankTransfer /></ProtectedRoute>} />
              <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/properties" element={<ProtectedRoute role="admin"><AdminProperties /></ProtectedRoute>} />
              <Route path="/admin/investors" element={<ProtectedRoute role="admin"><AdminInvestors /></ProtectedRoute>} />
              <Route path="/admin/kyc" element={<ProtectedRoute role="admin"><AdminKYC /></ProtectedRoute>} />
              <Route path="/admin/tokens" element={<ProtectedRoute role="admin"><AdminTokens /></ProtectedRoute>} />
              <Route path="/admin/bank-transfers" element={<ProtectedRoute role="admin"><AdminBankTransfers /></ProtectedRoute>} />
              <Route path="/admin/payment-settings" element={<ProtectedRoute role="admin"><AdminPaymentSettings /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0B1120", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" } }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
